import { InvestmentInstruction } from '../models/transactions/investment-instruction';
import { DateUtils } from './date-utils';
import { InvestmentInstructions } from '../models/transactions/investment-instructions';
import { MemberAccount } from '../models/transactions/member-account';
import { AccountTypeIdEnum } from '../models/enum/shared/account-type-id.enum';
import { GOVERNING_JURISDICTION_NAMES } from '../consts/consts';

export interface NamedAccountInvestmentInstruction {
  instruction: InvestmentInstructions;
  accountName: string;
}

export class InvestmentInstructionUtils {
  static distributionsAreEqual(instructions: InvestmentInstructions[]) {
    if (instructions.length <= 1) return true;

    const [firstInstructions, ...otherInstructions] = instructions;
    const sortedFirstInstructions = this.sortInstructions(firstInstructions.investmentInstructions);

    for (const instruction of otherInstructions) {
      const sortedInstructions = this.sortInstructions(instruction.investmentInstructions);
      if (sortedFirstInstructions?.length !== sortedInstructions?.length) return false;

      for (let i = 0; i < sortedFirstInstructions?.length; i++) {
        if (sortedFirstInstructions[i]?.fundId !== sortedInstructions[i]?.fundId) return false;
        if (sortedFirstInstructions[i]?.percentage !== sortedInstructions[i]?.percentage) return false;
      }
    }
    return true;
  }

  static sortInstructions(instructions: InvestmentInstruction[]) {
    return instructions?.slice()?.sort((a, b) => a?.fundId - b?.fundId);
  }

  static calculateLongestInstructionLength(investmentInstructions: InvestmentInstructions[]): number {
    return investmentInstructions.reduce((max, instructions) => {
      const instructionsLength = instructions?.investmentInstructions?.length ?? 0;
      return Math.max(max, instructionsLength);
    }, 0);
  }

  static instructionPercentByFundId(instructions: InvestmentInstruction[] | null, fundId: number): string {
    if (!instructions || instructions.length === 0) return '0.00%';
    const fundInstruction = instructions?.find(i => i?.fundId === fundId);
    return fundInstruction ? `${fundInstruction.percentage.toFixed(2)}%` : '0.00%';
  }

  static investmentInstructionsArePreviouslySet(instructions: InvestmentInstructions[]): boolean {
    return instructions?.some(i => !!i?.lastUpdatedDate) ?? false;
  }

  static getInvestmentInstructionsLastUpdatedDateText(instructions: InvestmentInstructions[]): string {
    const lastUpdatedDateString = instructions?.firstOrNull()?.lastUpdatedDate;
    const formattedDate = DateUtils.formatDateStringToCurrentDate(lastUpdatedDateString ?? '');
    return $localize`You last updated your investment instructions on ${formattedDate}`;
  }

  static withAccountDisplayNames(
    instructions: InvestmentInstructions[] | null,
    accounts: MemberAccount[] | null
  ): NamedAccountInvestmentInstruction[] {
    return (instructions ?? []).map(instruction => ({
      instruction,
      accountName: this.getAccountDisplayName(instruction, accounts ?? [])
    }));
  }

  static withAccountRowDisplayNames(
    instructions: InvestmentInstructions[] | null,
    accounts: MemberAccount[] | null
  ): NamedAccountInvestmentInstruction[] {
    return (instructions ?? []).map(instruction => ({
      instruction,
      accountName: this.getAccountRowDisplayName(instruction, accounts ?? [])
    }));
  }

  static getAccountDisplayName(instruction: InvestmentInstructions, accounts: MemberAccount[]): string {
    const account = this.findAccount(instruction, accounts);
    if (account?.typeId === AccountTypeIdEnum.VariableBenefit) {
      const jurisdictionName = account.governingJurisdictionId
        ? GOVERNING_JURISDICTION_NAMES[account.governingJurisdictionId]
        : 'Non-Locked-In';
      return $localize`${jurisdictionName} Variable Benefit Account`;
    }
    return instruction?.accountDetails?.type?.name ?? '';
  }

  // Variable Benefit accounts are identified by their jurisdiction, not their employer,
  // so they always use the account display name even when an employer is present.
  private static getAccountRowDisplayName(instruction: InvestmentInstructions, accounts: MemberAccount[]): string {
    const displayName = this.getAccountDisplayName(instruction, accounts);
    const isVariableBenefit = this.findAccount(instruction, accounts)?.typeId === AccountTypeIdEnum.VariableBenefit;
    if (isVariableBenefit) return displayName;
    return instruction?.accountDetails?.employer || displayName;
  }

  private static findAccount(
    instruction: InvestmentInstructions,
    accounts: MemberAccount[]
  ): MemberAccount | undefined {
    return accounts.find(a => a.id === instruction?.accountDetails?.accountId);
  }
}
